import {
  ChangeEvent,
  Dispatch,
  ReactElement,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import { Document, DocumentProps, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import type { OnItemClickArgs } from "react-pdf/src/shared/types";
import { useResizeDetector } from "react-resize-detector";
import { useDebounce } from "react-use";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";
import { FitScreen, ZoomIn, ZoomOut, ArrowUpward, ArrowDownward, Close } from "@mui/icons-material";
import { Box, IconButton, styled, Typography as T, Slide, OutlinedInput } from "@mui/material";
import { PDFDocumentProxy } from "pdfjs-dist";
import ErrorBoundary from "~/components/ErrorBoundary";

// Workaround for avoiding terminated pdfjs worker when reloading documents
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PDFViewerProps {
  DocumentProps: DocumentProps;
  loading?: ReactElement;
  fallback?: ReactElement;
}

interface SearchTermMatch {
  id: string;
  pageIndex: number;
  itemIndex: number;
  matchStart: number;
  matchLength: number;
}

interface TextItem {
  str: string;
  pageIndex: number;
  itemIndex: number;
}

const StyledDocument = styled(Document)({
  height: "100%",
  textAlign: "center",
  ".pdf-highlight": {
    color: "transparent",
    backgroundColor: "rgba(255,255,0,0.4)",
    "&.current-match": {
      backgroundColor: "rgba(255, 165, 0, 0.4)", // Orange background
    },
  },
});

const StyledPage = styled(Page)({
  paddingBottom: 16,
  display: "inline-block",
});

const StyledControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  zIndex: 2,
  display: "flex",
  alignItems: "center",
  userSelect: "none",
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  borderRadius: 4,
  backgroundColor: "rgba(0,0,0,0.5)",
  svg: {
    fontSize: 20,
    color: theme.palette.common.white,
  },
}));

interface SearchBarProps {
  matches: SearchTermMatch[];
  currentMatchIndex: number;
  handleSearchTermChange: (term: string) => void;
  handleMatchIndexChange: (index: number, matches: SearchTermMatch[]) => void;
}

const SearchBar = ({
  matches,
  currentMatchIndex,
  handleSearchTermChange,
  handleMatchIndexChange,
}: SearchBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [showSearch, setShowSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Debounce the search term change
  useDebounce(() => handleSearchTermChange(inputValue), 300, [inputValue]);

  const handleSearchClose = useCallback(() => {
    setShowSearch(false);
    handleSearchTermChange("");
  }, [handleSearchTermChange]);

  const handleNextMatch = () => {
    if (currentMatchIndex < matches.length - 1) {
      handleMatchIndexChange(currentMatchIndex + 1, matches);
    } else {
      // Loop back to beginning of doc
      handleMatchIndexChange(0, matches);
    }
  };

  const handlePrevMatch = () => {
    if (currentMatchIndex > 0) {
      handleMatchIndexChange(currentMatchIndex - 1, matches);
    } else {
      // Loop back to end of doc
      handleMatchIndexChange(matches.length - 1, matches);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setShowSearch(true);
        inputRef.current?.focus();
      }

      if (e.key === "Escape" && showSearch) {
        handleSearchClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSearchClose, showSearch]);

  return (
    <Slide
      direction="down"
      in={showSearch}
      onExited={() => setInputValue("")}
      mountOnEnter
      unmountOnExit
    >
      <StyledControls top={20} right={20} width={400}>
        <OutlinedInput
          autoFocus
          inputRef={inputRef}
          value={inputValue}
          placeholder="Search document"
          size="small"
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleNextMatch();
            }
          }}
          sx={{ bgcolor: "common.white" }}
          endAdornment={
            <T variant="body2" sx={{ fontSize: 12, textWrap: "nowrap", opacity: 0.7 }}>
              {matches.length > 0 ? currentMatchIndex + 1 : 0} of {matches.length}
            </T>
          }
        />
        <IconButton onClick={handlePrevMatch} disabled={matches.length <= 1}>
          <ArrowUpward />
        </IconButton>
        <IconButton onClick={handleNextMatch} disabled={matches.length <= 1}>
          <ArrowDownward />
        </IconButton>
        <IconButton onClick={handleSearchClose}>
          <Close />
        </IconButton>
      </StyledControls>
    </Slide>
  );
};

interface PageNumberInputProps {
  totalPages: number;
  currentPage: number | null;
  virtuosoRef: RefObject<VirtuosoHandle | null>;
}

const PageNumberInput = ({ currentPage, totalPages, virtuosoRef }: PageNumberInputProps) => {
  const [inputVal, setInputVal] = useState("");

  useEffect(() => {
    if (currentPage) {
      setInputVal(String(currentPage));
    }
  }, [currentPage]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === "") {
      setInputVal("");
    }
    if (!isNaN(Number(value))) {
      setInputVal(value);
    }
  };

  const handleSubmit = () => {
    if (inputVal) {
      const numericVal = Number(inputVal);
      const isValidRange = numericVal > 0 && numericVal <= totalPages;
      if (isValidRange) {
        virtuosoRef.current?.scrollToIndex({ index: numericVal - 1 });
        return;
      }
    }

    // Reset input value if update empty/invalid
    setInputVal(String(currentPage ?? ""));
  };

  return (
    <Box display="flex" alignItems="center" gap={1} color="common.white" fontSize={12}>
      <T variant="inherit">Page</T>
      <OutlinedInput
        type="numeric"
        value={inputVal}
        onChange={handleChange}
        onBlur={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleSubmit();
          }
        }}
        sx={{
          width: 30,
          bgcolor: "common.white",
          "& input": {
            padding: 0,
            fontSize: 12,
            textAlign: "center",
          },
          "& fieldset": {
            display: "none",
          },
        }}
      />
      <T variant="inherit">/ {totalPages}</T>
    </Box>
  );
};

interface PDFPageProps {
  index: number;
  scale: number;
  pageHeight: number;
  renderText: (textItem: TextItem) => string;
  setPageVisibility: Dispatch<SetStateAction<Record<number, number>>>;
}

const PDFPage = ({ index, scale, pageHeight, renderText, setPageVisibility }: PDFPageProps) => {
  const { ref: inViewRef } = useInView({
    threshold: Array.from({ length: 101 }, (_, i) => i / 100),
    onChange: (_, entry) => {
      setPageVisibility((prev) => {
        return { ...prev, [index]: entry.intersectionRatio };
      });
    },
  });

  return (
    <Box ref={inViewRef}>
      <StyledPage
        key={index}
        pageNumber={index + 1}
        scale={scale}
        // Avoid jumpiness in height when new pages are loading
        loading={<Box height={pageHeight * scale} />}
        // Handle search highlighting
        customTextRenderer={renderText}
      />
    </Box>
  );
};

const MIN_ZOOM = 0.75; // Minimum 75% zoom
const MAX_ZOOM = 3; // Maximum 300% zoom
const ZOOM_SCALE = 1.25;

/**
 * Helper function to handle extracting matches from the contents of each page
 * @param searchTerm - Term to match on (case insensitive)
 * @param pageIndex - Current page index
 * @param pdfDocument - PDF document state
 */
const getPageSearchMatches = async (
  searchTerm: string,
  pageIndex: number,
  pdfDocument: PDFDocumentProxy
): Promise<SearchTermMatch[]> => {
  const matches: SearchTermMatch[] = [];
  const pageNumber = pageIndex + 1;
  try {
    const page = await pdfDocument.getPage(pageNumber);
    const textContent = await page.getTextContent();

    textContent.items.forEach((textItem, itemIndex) => {
      const text = (textItem as any).str.toLowerCase();
      const searchRegex = new RegExp(searchTerm.toLowerCase(), "gi");
      const lineMatches = [...text.matchAll(searchRegex)];

      lineMatches.forEach((match) => {
        matches.push({
          id: `${pageIndex}-${itemIndex}-${match.index}`,
          pageIndex,
          itemIndex,
          matchStart: match.index ?? 0,
          matchLength: match[0].length,
        });
      });
    });
  } catch (err) {
    console.error(`Error searching page ${pageNumber}:`, err);
  }
  return matches;
};

/**
 * Helper function to handle marking highlighted text
 * @param textItem - Line of text on a page
 * @param matches - Current state of term matches (see: getPageSearchMatches)
 * @param currentMatchIndex - Current state of active highlight as user navigates through matches
 */
const renderTextWithHighlights = (
  textItem: TextItem,
  matches: SearchTermMatch[],
  currentMatchIndex: number
): string => {
  const { str, pageIndex, itemIndex } = textItem;

  const textItemMatches = matches.filter(
    (match) => match.pageIndex === pageIndex && match.itemIndex === itemIndex
  );

  if (textItemMatches.length === 0) {
    return str;
  }

  const parts = [];
  let lastIndex = 0;

  // Sort matches to process them in order
  textItemMatches.sort((a, b) => a.matchStart - b.matchStart);

  for (const match of textItemMatches) {
    // Append text before the match
    if (match.matchStart > lastIndex) {
      parts.push(str.substring(lastIndex, match.matchStart));
    }

    // Get the matched substring
    const matchText = str.substring(match.matchStart, match.matchStart + match.matchLength);
    const isCurrentMatch = matches[currentMatchIndex]?.id === match.id;
    const classes = isCurrentMatch ? "pdf-highlight current-match" : "pdf-highlight";

    parts.push(`<mark class="${classes}">${matchText}</mark>`);
    lastIndex = match.matchStart + match.matchLength;
  }

  // Append any remaining text after the last match
  if (lastIndex < str.length) {
    parts.push(str.substring(lastIndex));
  }

  return parts.join("");
};

const PDFViewer = ({ DocumentProps, loading, fallback }: PDFViewerProps) => {
  const [scale, setScale] = useState(1);
  const [fitVertical, setFitVertical] = useState(true);
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [matches, setMatches] = useState<SearchTermMatch[]>([]);
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageVisibility, setPageVisibility] = useState<Record<number, number>>({});
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const {
    width: containerWidth = 0,
    height: containerHeight = 0,
    ref,
  } = useResizeDetector({
    refreshMode: "throttle",
  });

  const currentPageIndex = useMemo(() => {
    let maxRatio = 0;
    let mostVisiblePage = 0;
    for (const [pageIndex, ratio] of Object.entries(pageVisibility)) {
      if (ratio > maxRatio) {
        maxRatio = ratio;
        mostVisiblePage = Number(pageIndex);
      }
    }

    if (!maxRatio) {
      // Pages still loading
      return null;
    }

    return mostVisiblePage;
  }, [pageVisibility]);

  // TODO: fix version difference between pdfjs and react-pdf
  // causing types to be incompatible
  const handleDocLoad = async (doc: any) => {
    setPdfDocument(doc);
    setTotalPages(doc.numPages);
    const firstPage = await doc.getPage(1);
    const viewport = firstPage.getViewport({ scale: 1 });
    setPageWidth(viewport.width);
    setPageHeight(viewport.height);

    // Compute initial scale
    if (fitVertical) {
      setScale(containerHeight / viewport.height);
    } else {
      setScale(containerWidth / viewport.width);
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev * ZOOM_SCALE, MAX_ZOOM));
    if (currentPageIndex !== null) {
      virtuosoRef.current?.scrollToIndex({ index: currentPageIndex });
    }
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev / ZOOM_SCALE, MIN_ZOOM));
    if (currentPageIndex !== null) {
      virtuosoRef.current?.scrollToIndex({ index: currentPageIndex });
    }
  };

  const snapToFitScreen = () => {
    if (!fitVertical) {
      // Fit to preview container height
      setScale(containerHeight / pageHeight);
    } else {
      // Fit to preview container width
      setScale(containerWidth / pageWidth);
    }

    // Flip toggle
    setFitVertical((prev) => !prev);

    // Ensure current page is kept during scale change
    if (currentPageIndex !== null) {
      virtuosoRef.current?.scrollToIndex({ index: currentPageIndex });
    }
  };

  const renderText = useCallback(
    (textItem: TextItem) => {
      if (searchTerm) {
        return renderTextWithHighlights(textItem, matches, searchMatchIndex);
      }

      return textItem.str;
    },
    [matches, searchTerm, searchMatchIndex]
  );

  const updateCurrentMatchIndex = (newIndex: number, matches: SearchTermMatch[]) => {
    setSearchMatchIndex(newIndex);
    // Scroll to the current match
    if (matches.length > 0 && newIndex >= 0 && newIndex < matches.length) {
      const match = matches[newIndex];
      virtuosoRef.current?.scrollToIndex({
        index: match.pageIndex,
        align: "center",
      });
    }
  };

  const handleSearchTermChange = async (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);

    if (!newSearchTerm || totalPages === 0 || !pdfDocument) {
      setSearchMatchIndex(0);
      setMatches([]);
      return;
    }

    const promises = Array.from({ length: totalPages }).map((_, pageIndex) =>
      getPageSearchMatches(newSearchTerm, pageIndex, pdfDocument)
    );

    // Get new matches across all pages
    const newMatches = (await Promise.all(promises)).flat();

    // Sort matches since each page was searched async/parallel
    newMatches.sort((a, b) => {
      if (a.pageIndex !== b.pageIndex) {
        return a.pageIndex - b.pageIndex;
      }
      if (a.itemIndex !== b.itemIndex) {
        return a.itemIndex - b.itemIndex;
      }
      return a.matchStart - b.matchStart;
    });

    const startingMatchIndex = newMatches.findIndex(
      (match) => match.pageIndex >= Number(currentPageIndex)
    );
    const initialMatchIndex = startingMatchIndex !== -1 ? startingMatchIndex : 0;
    updateCurrentMatchIndex(initialMatchIndex, newMatches);
    setMatches(newMatches);
  };

  return (
    <ErrorBoundary fallback={fallback}>
      <Box ref={ref} height="100%" width="100%" position="relative">
        <StyledDocument
          error={fallback}
          noData={loading}
          loading={loading}
          onItemClick={({ pageNumber }: OnItemClickArgs) =>
            // Handle scrolling internal links referencing page numbers
            virtuosoRef.current?.scrollToIndex({ index: Number(pageNumber) - 1 })
          }
          onLoadSuccess={handleDocLoad}
          externalLinkTarget="_blank"
          {...DocumentProps}
        >
          {pdfDocument && (
            <Virtuoso
              ref={virtuosoRef}
              totalCount={totalPages}
              increaseViewportBy={1000}
              rangeChanged={(range) =>
                // Zero out page visibility entries from map as they fall out of virtual list range
                setPageVisibility((prev) => {
                  const updated = { ...prev };
                  for (const key of Object.keys(prev)) {
                    const pageIndex = Number(key);
                    if (pageIndex < range.startIndex || pageIndex > range.endIndex) {
                      updated[pageIndex] = 0;
                    }
                  }
                  return updated;
                })
              }
              itemContent={(pageIndex) => (
                <PDFPage
                  key={pageIndex}
                  scale={scale}
                  index={pageIndex}
                  pageHeight={pageHeight}
                  renderText={renderText}
                  setPageVisibility={setPageVisibility}
                />
              )}
            />
          )}
        </StyledDocument>

        {pdfDocument && (
          <>
            <SearchBar
              matches={matches}
              currentMatchIndex={searchMatchIndex}
              handleSearchTermChange={handleSearchTermChange}
              handleMatchIndexChange={updateCurrentMatchIndex}
            />

            <StyledControls right={20} bottom={20}>
              <PageNumberInput
                currentPage={currentPageIndex !== null ? currentPageIndex + 1 : null}
                totalPages={totalPages}
                virtuosoRef={virtuosoRef}
              />
              <IconButton onClick={handleZoomOut} size="small" disabled={scale === MIN_ZOOM}>
                <ZoomOut />
              </IconButton>
              <IconButton onClick={handleZoomIn} size="small" disabled={scale === MAX_ZOOM}>
                <ZoomIn />
              </IconButton>
              <IconButton onClick={snapToFitScreen} size="small">
                <FitScreen
                  sx={{
                    transition: "all .3s ease",
                    transform: fitVertical ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
              </IconButton>
            </StyledControls>
          </>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default PDFViewer;
